import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import {
  LOT_EXPIRY_BUCKETS,
  type LotExpiryBucket,
  type StockFilter,
} from './inventory-filters'

/**
 * Filter rail for /inventory.
 *
 * Module-scoped, mirroring the MarginsFilterRail collapse-toggle
 * pattern. Two filter sections are rendered conditionally on the
 * current `viewMode`:
 *   - stock view → all / in-stock / out-of-stock toggles
 *   - lots view → expiry window bucket (all / expired / ≤30d / ≤60d)
 *
 * The rail does NOT own the search input or the view toggle — those
 * live in the page Toolbar where Chris uses them most frequently.
 * The rail is for narrowing-by-attribute filters that aren't on a
 * per-keystroke loop.
 *
 * Constants + filter types are in `./inventory-filters.ts` (Fast
 * Refresh contract).
 */

export interface InventoryFilterRailProps {
  viewMode: 'stock' | 'lots'
  stockFilter: StockFilter
  onStockFilterChange: (next: StockFilter) => void
  lotBucket: LotExpiryBucket
  onLotBucketChange: (next: LotExpiryBucket) => void
  collapsed: boolean
  onToggleCollapsed: () => void
}

/** Count of active narrowing filters; used for the collapsed-rail badge. */
function activeCount(props: InventoryFilterRailProps): number {
  let n = 0
  if (props.viewMode === 'stock' && props.stockFilter !== 'all') n += 1
  if (props.viewMode === 'lots' && props.lotBucket !== 'all') n += 1
  return n
}

export function InventoryFilterRail(props: InventoryFilterRailProps) {
  const {
    viewMode,
    stockFilter,
    onStockFilterChange,
    lotBucket,
    onLotBucketChange,
    collapsed,
    onToggleCollapsed,
  } = props

  if (collapsed) {
    const n = activeCount(props)
    return (
      <aside
        className="flex w-8 flex-col items-center border-r border-border-subtle bg-surface-sunk"
        aria-label="Filters (collapsed)"
      >
        <button
          type="button"
          aria-label="Expand filters"
          onClick={onToggleCollapsed}
          className="mt-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronRight size={14} />
        </button>
        {n > 0 ? (
          <span
            className="text-role-badge-count mt-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-subtle px-1.5 text-accent-text"
            aria-label={`${n} active filter${n === 1 ? '' : 's'}`}
          >
            {n}
          </span>
        ) : null}
      </aside>
    )
  }

  return (
    <aside
      className="flex w-[220px] flex-shrink-0 flex-col border-r border-border-subtle bg-surface-sunk"
      aria-label="Filters"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
        <span className="text-role-section-label">Filters</span>
        <button
          type="button"
          aria-label="Collapse filters"
          onClick={onToggleCollapsed}
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronLeft size={14} />
        </button>
      </header>

      <div className="flex flex-col gap-3 p-3">
        {viewMode === 'stock' ? (
          <section className="flex flex-col gap-1.5">
            <span className="text-role-section-label">Availability</span>
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  { value: 'all' as const, label: 'All' },
                  { value: 'in_stock' as const, label: 'In stock' },
                  { value: 'out_of_stock' as const, label: 'Out of stock' },
                ]
              ).map((f) => {
                const active = stockFilter === f.value
                return (
                  <button
                    key={f.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onStockFilterChange(f.value)}
                    className={cn(
                      'inline-flex items-center rounded-xs border border-border-default bg-surface px-2 py-0.5 text-xs text-primary hover:bg-surface-hover',
                      'aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text',
                    )}
                  >
                    {f.label}
                  </button>
                )
              })}
            </div>
            <p className="text-role-metadata mt-1 text-tertiary">
              Filters by available qty (on-hand minus reserved).
            </p>
          </section>
        ) : (
          <section className="flex flex-col gap-1.5">
            <span className="text-role-section-label">Expiry window</span>
            <div className="flex flex-wrap gap-1.5">
              {LOT_EXPIRY_BUCKETS.map((b) => {
                const active = lotBucket === b.value
                return (
                  <button
                    key={b.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onLotBucketChange(b.value)}
                    className={cn(
                      'inline-flex items-center rounded-xs border border-border-default bg-surface px-2 py-0.5 text-xs text-primary hover:bg-surface-hover',
                      'aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text',
                    )}
                  >
                    {b.label}
                  </button>
                )
              })}
            </div>
            <p className="text-role-metadata mt-1 text-tertiary">
              Backend loads up to 90 days of expiring lots; this narrows further.
            </p>
          </section>
        )}
      </div>
    </aside>
  )
}
